import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Briefcase, IndianRupee, Filter as FilterIcon } from "lucide-react";

const TYPES = ["All", "Urgent", "Active Hiring", "Full Time", "Contract"];

function JobCard({ job }) {
  const navigate = useNavigate();
  const tagColor = job.job_type === "Urgent"
    ? "bg-bee-orange/10 text-bee-orange"
    : "bg-bee-teal/10 text-bee-teal";
  return (
    <div
      data-testid={`job-card-${job.id}`}
      className="rounded-2xl border border-border bg-card p-5 sm:p-6 card-lift flex flex-col"
    >
      <div className="flex items-start justify-between gap-3">
        <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase font-bold tracking-wider ${tagColor}`}>
          {job.job_type}
        </span>
        {job.domain && (
          <span className="text-xs text-muted-foreground">{job.domain}</span>
        )}
      </div>
      <h3 className="mt-3 font-display text-xl font-bold tracking-tight">{job.title}</h3>
      <div className="mt-3 space-y-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2"><Briefcase className="h-3.5 w-3.5" /> {job.experience}</div>
        <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {(job.locations || []).join(", ")}</div>
        {job.ctc && <div className="flex items-center gap-2"><IndianRupee className="h-3.5 w-3.5" /> {job.ctc}</div>}
      </div>
      {job.skills?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {job.skills.slice(0, 4).map((s) => (
            <span key={s} className="px-2 py-0.5 rounded-full text-[11px] bg-secondary text-foreground/80 font-medium">{s}</span>
          ))}
        </div>
      )}
      <div className="mt-5 flex gap-2">
        <Button
          variant="outline"
          onClick={() => navigate(`/jobs/${job.id}`)}
          data-testid={`job-view-${job.id}`}
          className="flex-1 rounded-full border-bee-teal/30 text-bee-teal hover:bg-bee-teal hover:text-white"
        >
          Description
        </Button>
        <Button
          onClick={() => navigate(`/jobs/${job.id}?apply=1`)}
          data-testid={`job-apply-${job.id}`}
          className="flex-1 rounded-full bg-bee-teal text-white hover:bg-[hsl(var(--bee-teal-dark))]"
        >
          Apply Now
        </Button>
      </div>
    </div>
  );
}

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [type, setType] = useState("All");

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (location) params.location = location;
      if (type && type !== "All") params.job_type = type;
      const { data } = await api.get("/jobs", { params });
      setJobs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    fetchJobs();
  };

  return (
    <>
      <Navbar />
      <main data-testid="jobs-page" className="pt-[72px]">
        {/* Header */}
        <section className="bg-gradient-to-br from-bee-teal to-[hsl(var(--bee-teal-dark))] text-white py-14 lg:py-20 relative overflow-hidden">
          <div className="absolute inset-0 honeycomb-divider opacity-25" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-xs uppercase tracking-[0.2em] text-bee-honey font-semibold mb-3">Current Openings</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
              Find your <span className="text-bee-honey">next role</span>.
            </h1>
            <p className="mt-4 text-white/80 max-w-2xl">
              Browse {jobs.length} active opportunities curated by our recruiters across IT, BFSI, Sales, and more.
            </p>

            <form onSubmit={onSearch} className="mt-8 grid md:grid-cols-12 gap-3 p-3 rounded-2xl bg-white/95 backdrop-blur" data-testid="jobs-filter-bar">
              <div className="md:col-span-5 flex items-center gap-2 px-3 rounded-xl bg-secondary">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Job title or keyword"
                  className="border-0 bg-transparent focus-visible:ring-0 text-foreground"
                  data-testid="jobs-search-input"
                />
              </div>
              <div className="md:col-span-4 flex items-center gap-2 px-3 rounded-xl bg-secondary">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Location"
                  className="border-0 bg-transparent focus-visible:ring-0 text-foreground"
                  data-testid="jobs-location-input"
                />
              </div>
              <div className="md:col-span-2">
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="bg-secondary border-0 text-foreground" data-testid="jobs-type-select">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" data-testid="jobs-search-btn" className="md:col-span-1 bg-bee-teal text-white hover:bg-[hsl(var(--bee-teal-dark))] rounded-xl">
                Search
              </Button>
            </form>
          </div>
        </section>

        {/* List */}
        <section className="py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-20 text-muted-foreground" data-testid="jobs-loading">Loading jobs...</div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-20" data-testid="jobs-empty">
                <FilterIcon className="h-10 w-10 text-muted-foreground/40 mx-auto" />
                <div className="mt-3 font-display text-xl font-bold">No matching jobs</div>
                <div className="text-sm text-muted-foreground mt-1">Try adjusting your filters.</div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {jobs.map((j) => <JobCard key={j.id} job={j} />)}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
